import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { COMPLETE_MENU_STRUCTURE, type MenuNode } from '@/config/menuStructure';

const SUPABASE_URL = 'https://hjodtxoylcvcwjgvuctk.supabase.co';
const MIN_TRACKING_SECONDS = 5;

interface RouteLabel {
  path: string;
  label: string;
}

// Flatten menu structure to get route labels
const flattenMenuLabels = (nodes: MenuNode[], result: RouteLabel[] = []): RouteLabel[] => {
  for (const node of nodes) {
    if (node.path) {
      result.push({ path: node.path, label: node.label });
    }
    if (node.children) {
      flattenMenuLabels(node.children, result);
    }
  }
  return result;
};

const allRouteLabels = flattenMenuLabels(COMPLETE_MENU_STRUCTURE);

// Find label for a route
const findRouteLabel = (pathname: string): string | undefined => {
  // Exact match first
  const exact = allRouteLabels.find(r => r.path === pathname);
  if (exact) return exact.label;

  // Prefix match for dynamic routes
  const sorted = [...allRouteLabels].sort((a, b) => b.path.length - a.path.length);
  for (const route of sorted) {
    if (pathname.startsWith(route.path + '/') || pathname.startsWith(route.path + '?')) {
      return route.label;
    }
  }
  return undefined;
};

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('activity_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('activity_session_id', sessionId);
  }
  return sessionId;
};

export const useActivityTracking = () => {
  const location = useLocation();
  const { currentBrandId } = useBrandManager();
  
  // Use refs to avoid re-renders and maintain state across renders
  const routeEntryTime = useRef<number>(0);
  const accumulatedSeconds = useRef<number>(0);
  const currentPath = useRef<string>('');
  const isVisible = useRef<boolean>(true);
  const sessionId = useRef<string>(getSessionId());
  const userId = useRef<string | null>(null);
  const lastBrandId = useRef<string | null>(null);

  // Track time via Supabase client (for regular updates)
  const trackActivity = useCallback(async (
    path: string,
    seconds: number,
    brandId: string
  ) => {
    if (!userId.current || seconds < MIN_TRACKING_SECONDS) return;

    const label = findRouteLabel(path);

    try {
      // First check if record exists
      const { data: existing, error: selectError } = await supabase
        .from('os_universal_activity_tracking')
        .select('id, total_seconds, visit_count')
        .eq('session_id', sessionId.current)
        .eq('user_id', userId.current)
        .eq('brand_id', brandId)
        .eq('route_path', path)
        .maybeSingle();

      if (selectError) throw selectError;

      if (existing) {
        // Update existing
        await supabase
          .from('os_universal_activity_tracking')
          .update({
            total_seconds: existing.total_seconds + seconds,
            visit_count: existing.visit_count + 1,
            route_label: label || null,
            last_visited_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
      } else {
        // Insert new
        await supabase
          .from('os_universal_activity_tracking')
          .insert({
            session_id: sessionId.current,
            user_id: userId.current,
            brand_id: brandId,
            route_path: path,
            route_label: label || null,
            total_seconds: seconds,
            visit_count: 1,
            last_visited_at: new Date().toISOString(),
          });
      }
    } catch (err) {
      console.error('Error tracking activity:', err);
    }
  }, []);

  // Send beacon on page close (reliable delivery)
  const sendBeacon = useCallback((path: string, seconds: number, brandId: string) => {
    if (!userId.current || seconds < MIN_TRACKING_SECONDS) return;

    const label = findRouteLabel(path);
    const payload = JSON.stringify({
      session_id: sessionId.current,
      user_id: userId.current,
      brand_id: brandId,
      route_path: path,
      route_label: label,
      seconds,
    });

    const url = `${SUPABASE_URL}/functions/v1/track-activity`;
    
    // navigator.sendBeacon is more reliable for page unload
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, payload);
    }
  }, []);

  // Calculate and save accumulated time
  const saveAccumulatedTime = useCallback(() => {
    if (!currentPath.current || !lastBrandId.current) return;
    
    const now = Date.now();
    if (isVisible.current && routeEntryTime.current > 0) {
      accumulatedSeconds.current += Math.floor((now - routeEntryTime.current) / 1000);
    }
    
    const seconds = accumulatedSeconds.current;
    const path = currentPath.current;
    const brandId = lastBrandId.current;
    
    // Reset
    accumulatedSeconds.current = 0;
    routeEntryTime.current = now;
    
    return { path, seconds, brandId };
  }, []);

  // Get user ID on mount
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      userId.current = user?.id || null;
    };
    getUser();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      userId.current = session?.user?.id || null;
    });

    return () => subscription.unsubscribe();
  }, []);

  // Handle visibility changes (pause/resume timer)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const now = Date.now();
      
      if (document.hidden) {
        // Pause: save elapsed time
        if (isVisible.current && routeEntryTime.current > 0) {
          accumulatedSeconds.current += Math.floor((now - routeEntryTime.current) / 1000);
        }
        isVisible.current = false;
      } else {
        // Resume: restart timer
        isVisible.current = true;
        routeEntryTime.current = now;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Handle page close
  useEffect(() => {
    const handleBeforeUnload = () => {
      const result = saveAccumulatedTime();
      if (result && result.seconds >= MIN_TRACKING_SECONDS) {
        sendBeacon(result.path, result.seconds, result.brandId);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveAccumulatedTime, sendBeacon]);

  // Handle route changes
  useEffect(() => {
    const pathname = location.pathname;
    
    // Skip if no brand or no user
    if (!currentBrandId || !userId.current) {
      currentPath.current = pathname;
      lastBrandId.current = currentBrandId || null;
      routeEntryTime.current = Date.now();
      return;
    }

    // If route changed, save previous route's time
    if (currentPath.current && currentPath.current !== pathname && lastBrandId.current) {
      const result = saveAccumulatedTime();
      if (result && result.seconds >= MIN_TRACKING_SECONDS) {
        trackActivity(result.path, result.seconds, result.brandId);
      }
    }

    // Start tracking new route
    currentPath.current = pathname;
    lastBrandId.current = currentBrandId;
    routeEntryTime.current = Date.now();
    accumulatedSeconds.current = 0;
    isVisible.current = !document.hidden;
  }, [location.pathname, currentBrandId, saveAccumulatedTime, trackActivity]);
};
